import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { containsNoValues } from 'src/util/object.helpers'
import {
  checkValidations,
  emailRule,
  passwordRule,
  requiredRule
} from 'src/util/validation/commonValidation.helpers'
import UserAccount from '../components/userAccount/UserAccount'
import { ACCOUNT_PROFILE_DISPATCH } from '../store/reducers/account/account'

import {
  changeAccountInputAndErrorState,
  changeAccountState,
  refreshKYCInfo,
  resetAccountState,
  updateUserAccount
} from '../store/actions/account'
import {
  changePassword,
  updateUserAttributes,
  changeEmail
} from '../store/actions/auth'

import {
  ACCOUNT_CHANGE_ACTION,
  AUTH_FIRST_NAME_ATTR,
  AUTH_LAST_NAME_ATTR
} from '../constants/auth'
import { ERROR_TEXT } from '../constants/messages'
import { extractCognitoUserSocialIdentity } from '../util/auth.helpers'
import { deepEqualObjects } from '../util/object.helpers'
import {
  buildSettingsFilterOptions,
  SETTING_FILTER_TYPE
} from '../util/settings.helpers'
import { KYC_STAGE } from '../util/kyc.helpers'

const UserAccountContainer = ({
  accountProfile,
  changeAccountState,
  changeAccountInputAndErrorState,
  resetAccountState,
  updateUserAttributes,
  changePassword,
  authProfile,
  isExchangeEnabled,
  updateUserAccount,
  kycProfile,
  refreshKYCInfo,
  initKYCProcess,
  changeEmail
}) => {
  const { t } = useTranslation()
  const signedUpVia = useMemo(() => {
    return extractCognitoUserSocialIdentity(authProfile)
  }, [authProfile])

  const location = useLocation()

  useEffect(() => {
    return () => {
      resetAccountState()
    }
  }, [])

  const [filterBy, setFilterBy] = useState(
    location?.state?.action || SETTING_FILTER_TYPE.GENERAL
  )

  const [authAction, setAuthAction] = useState(null)

  const handleChangePersonalInfo = () => {
    setAuthAction(ACCOUNT_CHANGE_ACTION.PERSONAL_INFO)
    resetErrorSuccess()
    const { firstNameTemp, lastNameTemp, firstName, lastName } = accountProfile
    const errors = checkValidations({
      firstNameTemp: { value: firstNameTemp, rules: [requiredRule] },
      lastNameTemp: { value: lastNameTemp, rules: [requiredRule] }
    })
    if (containsNoValues(errors)) {
      const userInfoUpdated = !deepEqualObjects(
        { firstNameTemp, lastNameTemp },
        { firstNameTemp: firstName, lastNameTemp: lastName }
      )
      if (userInfoUpdated) {
        const attributes = {
          [AUTH_LAST_NAME_ATTR]: lastNameTemp,
          [AUTH_FIRST_NAME_ATTR]: firstNameTemp
        }
        updateUserAttributes(attributes)
      }
    } else {
      changeAccountState(ACCOUNT_PROFILE_DISPATCH.ERROR, errors)
    }
  }

  const handleChangeEmailAddress = () => {
    setAuthAction(ACCOUNT_CHANGE_ACTION.EMAIL)
    resetErrorSuccess()
    const { emailTemp, email } = accountProfile
    const errors = checkValidations({
      emailTemp: { value: emailTemp, rules: [requiredRule, emailRule] }
    })
    if (containsNoValues(errors)) {
      if (emailTemp !== email) {
        changeEmail(emailTemp)
      }
    } else {
      changeAccountState(ACCOUNT_PROFILE_DISPATCH.ERROR, errors)
    }
  }

  const handleChangePassword = () => {
    setAuthAction(ACCOUNT_CHANGE_ACTION.PASSWORD)
    resetErrorSuccess()
    const { oldPassword, newPassword } = accountProfile
    const errors = checkValidations({
      oldPassword: {
        value: oldPassword,
        rules: [requiredRule, passwordRule]
      },
      newPassword: { value: newPassword, rules: [requiredRule, passwordRule] }
    })
    if (containsNoValues(errors)) {
      if (oldPassword !== newPassword) {
        changePassword(oldPassword, newPassword)
      } else {
        changeAccountState(ACCOUNT_PROFILE_DISPATCH.ERROR, {
          newPassword: ERROR_TEXT.OLD_NEW_PASSWORDS_SAME
        })
      }
    } else {
      changeAccountState(ACCOUNT_PROFILE_DISPATCH.ERROR, errors)
    }
  }

  const handleChangeCountry = () => {
    setAuthAction(ACCOUNT_CHANGE_ACTION.COUNTRY)
    resetErrorSuccess()
    const { country, countryTemp } = accountProfile
    const errors = checkValidations({
      countryTemp: { value: countryTemp, rules: [requiredRule] }
    })
    if (containsNoValues(errors)) {
      const userInfoUpdated = !deepEqualObjects(
        { countryTemp },
        { countryTemp: country }
      )
      if (userInfoUpdated) {
        updateUserAccount({ country_ISO: countryTemp })
      }
    } else {
      changeAccountState(ACCOUNT_PROFILE_DISPATCH.ERROR, errors)
    }
  }

  const resetErrorSuccess = () => {
    changeAccountState(ACCOUNT_PROFILE_DISPATCH.ERROR, {})
    changeAccountState(ACCOUNT_PROFILE_DISPATCH.SUCCESS, {})
  }

  const handleChangeFilterMenu = ({ value }) => {
    resetAccountState()
    setFilterBy(value)
  }

  return (
    <UserAccount
      accountProfile={accountProfile}
      dispatchAction={changeAccountInputAndErrorState}
      updatePersonalInfo={handleChangePersonalInfo}
      changeEmailAddress={handleChangeEmailAddress}
      changePassword={handleChangePassword}
      changeCountry={handleChangeCountry}
      t={t}
      signedUpVia={signedUpVia}
      tabOptions={buildSettingsFilterOptions({ t, isExchangeEnabled })}
      filterBy={filterBy}
      onChangeFilterMenu={handleChangeFilterMenu}
      authAction={authAction}
      kycProfile={kycProfile}
      isKycSuccess={kycProfile.status === KYC_STAGE.SUCCESS}
      onContinueKYC={initKYCProcess}
      onRefreshKYC={refreshKYCInfo}
    />
  )
}

const mapStateToProps = ({ accountReducer, authReducer }) => {
  const { accountProfile, kycProfile } = accountReducer
  const { authProfile } = authReducer
  return {
    accountProfile,
    kycProfile,
    authProfile
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      changeAccountState,
      changeAccountInputAndErrorState,
      resetAccountState,
      updateUserAttributes,
      changePassword,
      updateUserAccount,
      refreshKYCInfo,
      changeEmail
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserAccountContainer)
